/**
 * DocumentRow Component
 *
 * Displays a single document with name, size, date, and download button
 */

import { Download, FileText, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDate, formatFileSize, formatMimeType } from '@/utils/formatters';
import type { PortalDocument } from '../services/documents-service';

interface DocumentRowProps {
  document: PortalDocument;
  onDownload: (documentId: string, filename: string) => void;
  isDownloading: boolean;
}

export function DocumentRow({
  document,
  onDownload,
  isDownloading,
}: DocumentRowProps) {
  const handleDownload = () => {
    onDownload(document.id, document.original_filename);
  };

  return (
    <div className="flex items-center justify-between py-1.5 px-3 hover:bg-muted/50 rounded-md transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {document.is_new && (
          <Badge className="flex-shrink-0 px-1.5 py-0 text-[10px]">New</Badge>
        )}
        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span
          className="text-sm font-medium text-foreground truncate cursor-pointer hover:underline"
          onClick={handleDownload}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleDownload()}
        >
          {document.name}
        </span>
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {formatMimeType(document.mime_type)} &middot;{' '}
          {formatFileSize(document.file_size)} &middot;{' '}
          {formatDate(document.upload_date)}
        </span>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleDownload}
        disabled={isDownloading}
        className="h-8 w-8 flex-shrink-0"
      >
        {isDownloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        <span className="sr-only">Download {document.name}</span>
      </Button>
    </div>
  );
}
